/**
 * Accurate-enough outline of the Kingdom of Saudi Arabia as [lon, lat] pairs,
 * traced clockwise from the Gulf of Aqaba (NW) around Jordan/Iraq (N), the Gulf
 * with the Qatar indentation and UAE (E), Oman + the Empty Quarter (SE), the
 * Yemen border (S), and up the Red Sea coast (W). Normalized to a [0..1] box at
 * runtime so the shape auto-fits. Powers the dotted-map field (plan §6, CH0).
 */
const SAUDI_LONLAT: ReadonlyArray<readonly [number, number]> = [
  [34.9, 29.2], // Haql — Gulf of Aqaba (NW)
  [36.1, 29.6],
  [37.0, 30.5],
  [37.5, 31.0],
  [38.0, 31.5],
  [38.99, 31.99], // Jordan / Iraq tripoint
  [39.3, 32.0],
  [40.0, 31.9],
  [41.0, 31.4],
  [42.0, 31.1],
  [43.0, 30.5],
  [44.5, 29.6],
  [46.0, 29.1],
  [47.0, 28.9],
  [47.7, 28.5], // Kuwait
  [48.4, 28.5],
  [49.0, 28.0],
  [49.6, 27.0],
  [50.0, 26.6], // Dammam / Gulf coast
  [50.2, 26.1],
  [50.8, 25.6],
  [51.0, 25.0], // Qatar base (Salwa)
  [51.28, 24.6], // Qatar peninsula tip
  [51.0, 24.25], // Qatar bay
  [51.6, 24.1], // UAE border
  [54.0, 24.0],
  [55.2, 22.6], // UAE / Oman
  [55.4, 21.5],
  [55.0, 20.0], // Oman / Empty Quarter
  [53.0, 19.0],
  [51.0, 18.8],
  [49.0, 18.6], // Yemen / Oman corner
  [47.0, 17.5], // Yemen border
  [45.2, 17.3],
  [43.2, 17.3],
  [42.8, 16.7],
  [42.6, 16.4], // Jazan (SW)
  [41.8, 17.6], // Red Sea coast (up)
  [41.2, 18.6],
  [40.5, 19.8],
  [39.8, 20.8],
  [39.1, 22.0], // Jeddah
  [38.5, 23.0],
  [37.8, 24.0], // Yanbu
  [37.0, 25.0],
  [36.2, 26.2],
  [35.5, 27.2],
  [35.0, 28.3],
];

// normalize to [0..1] (x: west→east, y: north→south) filling the box
const lons = SAUDI_LONLAT.map((p) => p[0]);
const lats = SAUDI_LONLAT.map((p) => p[1]);
const minLon = Math.min(...lons);
const maxLon = Math.max(...lons);
const minLat = Math.min(...lats);
const maxLat = Math.max(...lats);

export const SAUDI_POLYGON: ReadonlyArray<readonly [number, number]> = SAUDI_LONLAT.map(
  ([lon, lat]) => [(lon - minLon) / (maxLon - minLon), (maxLat - lat) / (maxLat - minLat)] as const,
);

function pointInPolygon(x: number, y: number, poly: ReadonlyArray<readonly [number, number]>): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Shortest distance from a point to the polygon edges (normalized units). */
function distToPolygon(px: number, py: number, poly: ReadonlyArray<readonly [number, number]>): number {
  let min = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[j];
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy || 1;
    let t = ((px - x1) * dx + (py - y1) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const d = Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
    if (d < min) min = d;
  }
  return min;
}

export interface MapPoint {
  x: number; // normalized 0..1 within the map's own box
  y: number;
  phase: number; // per-point twinkle phase
  edge: number; // 0..1 — brighter near the border
}

/**
 * Sample a jittered grid of points inside the Saudi polygon. Deterministic
 * jitter (index-derived) keeps the cloud stable across resizes (plan §6:
 * zero per-frame allocation once built).
 */
export function sampleSaudiPoints(density = 92): MapPoint[] {
  const pts: MapPoint[] = [];
  const step = 1 / density;
  for (let gy = 0; gy <= density; gy++) {
    for (let gx = 0; gx <= density; gx++) {
      const jx = (Math.sin(gx * 12.9898 + gy * 78.233) * 43758.5453) % 1;
      const jy = (Math.sin(gx * 39.3468 + gy * 11.135) * 24634.6345) % 1;
      const x = gx * step + (jx - 0.5) * step * 0.85;
      const y = gy * step + (jy - 0.5) * step * 0.85;
      if (!pointInPolygon(x, y, SAUDI_POLYGON)) continue;
      const d = distToPolygon(x, y, SAUDI_POLYGON);
      pts.push({
        x,
        y,
        phase: Math.abs(jx) * Math.PI * 2,
        edge: Math.max(0, 1 - d * 9), // sharp border emphasis
      });
    }
  }
  return pts;
}
